import { useState } from 'react'
import { CheckCircle2, Info, Plus } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'
import {
  useSessionRoster, useTrainingCatalogueMutations, useTrainingCourses, useTrainingNeeds,
  useTrainingSessions,
} from '../api'
import {
  NOMINATION_SOURCE_LABELS, NOMINATION_STATUS_LABELS, SESSION_STATUS_LABELS,
} from '../types'
import type {
  NominationSource, NominationStatus, SessionCompletionView, SessionStatus,
} from '../types'

const SESSION_TONE: Record<SessionStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  PLANNED: 'neutral',
  OPEN: 'info',
  RUNNING: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
}

const NOMINATION_TONE: Record<NominationStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'info',
  REJECTED: 'danger',
  WITHDRAWN: 'neutral',
  ATTENDED: 'success',
  NO_SHOW: 'danger',
}

/** Sessions, their rosters, attendance and completion. */
export function TrainingSessionsPage() {
  useBreadcrumb()
  const sessions = useTrainingSessions()
  const courses = useTrainingCourses()
  const needs = useTrainingNeeds()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const {
    saveSession, openSession, cancelSession, completeSession, nominate,
    submitNomination, decideNomination, recordAttendance,
  } = useTrainingCatalogueMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const roster = useSessionRoster(selected)

  const [draft, setDraft] = useState<{
    courseId: string; startsOn: string; endsOn: string; location: string; trainer: string
    capacity: string; costPerHead: string; currency: string
  } | null>(null)
  const [nominateDraft, setNominateDraft] = useState<{
    employeeId: string; trainingNeedId: string
  } | null>(null)
  const [cancelDraft, setCancelDraft] = useState<{ id: string; reason: string } | null>(null)
  const [completion, setCompletion] = useState<SessionCompletionView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const chosen = sessions.data?.find((session) => session.id === selected)

  /**
   * Runs one call and puts the refusal where the person is looking.
   *
   * A message shown in the dialog and again in a banner behind it reads as two things having
   * gone wrong, so a failure inside a form stays inside that form.
   */
  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Training sessions"
        subtitle="Who is booked on what, who turned up, and what it produced."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                courseId: courses.data?.[0]?.id ?? '', startsOn: '', endsOn: '',
                location: '', trainer: '', capacity: '', costPerHead: '', currency: 'AED',
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New session
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      {completion && (
        <Card className="space-y-1 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="flex items-start gap-2 font-medium">
            <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Session completed
          </p>
          <p>
            {completion.attended} attended, {completion.noShow} did not,{' '}
            {completion.certificatesIssued} certificate(s) issued,{' '}
            {completion.needsClosed} training need(s) closed.
          </p>
          {completion.note && <p>{completion.note}</p>}
        </Card>
      )}

      <Card>
        {sessions.isPending && <Skeleton className="m-4 h-24" />}
        {sessions.isError && (
          <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />
        )}
        {sessions.isSuccess && sessions.data.length === 0 && (
          <EmptyState
            title="No session is scheduled"
            description="A session is one delivery of a course: dates, a place, a capacity and a cost."
          />
        )}
        {sessions.isSuccess && sessions.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Course</th>
                  <th className="p-3 text-start font-medium">When</th>
                  <th className="p-3 text-end font-medium">Places</th>
                  <th className="p-3 text-end font-medium">Cost a head</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {sessions.data.map((session) => (
                  <tr key={session.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <button
                        type="button"
                        className="font-mono underline underline-offset-2"
                        onClick={() => setSelected(session.id)}
                      >
                        {session.reference}
                      </button>
                    </td>
                    <td className="p-3">{session.courseTitle ?? '—'}</td>
                    <td className="p-3 text-slate-600">
                      {session.startsOn} → {session.endsOn}
                    </td>
                    <td className="p-3 text-end">
                      {session.placesLeft == null
                        ? `${session.placesTaken} (uncapped)`
                        : `${session.placesTaken} of ${session.capacity}`}
                    </td>
                    <td className="p-3 text-end">
                      <CurrencyValue
                        value={session.costPerHead}
                        currency={session.currency ?? undefined}
                      />
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={session.status}
                        tone={SESSION_TONE[session.status as SessionStatus] ?? 'neutral'}
                        label={SESSION_STATUS_LABELS[session.status as SessionStatus]
                          ?? session.status}
                      />
                    </td>
                    <td className="p-3 text-end">
                      <div className="flex justify-end gap-2">
                        {session.status === 'PLANNED' && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => act(
                              () => openSession.mutateAsync(session.id),
                              'That session could not be opened')}
                          >
                            Open
                          </Button>
                        )}
                        {(session.status === 'OPEN' || session.status === 'RUNNING') && (
                          <>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setError(null)
                                setCancelDraft({ id: session.id, reason: '' })
                              }}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              onClick={() => act(async () => {
                                const result = await completeSession.mutateAsync(session.id)
                                setCompletion(result)
                              }, 'That session could not be completed')}
                            >
                              Complete
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {chosen && (
        <>
          <PageHeader
            title={`Roster for ${chosen.reference}`}
            subtitle={`${chosen.courseTitle ?? ''} · ${chosen.startsOn} → ${chosen.endsOn}`}
            actions={
              (chosen.status === 'OPEN' || chosen.status === 'PLANNED') && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setError(null)
                    setNominateDraft({ employeeId: '', trainingNeedId: '' })
                  }}
                >
                  <Plus className="size-4" aria-hidden="true" />
                  Nominate
                </Button>
              )
            }
          />
          <Card>
            {roster.isPending && <Skeleton className="m-4 h-20" />}
            {roster.isSuccess && roster.data.length === 0 && (
              <EmptyState
                title="Nobody is nominated"
                description="Nominate people from the training needs the appraisals raised, or by hand."
              />
            )}
            {roster.isSuccess && roster.data.length > 0 && (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Person</th>
                      <th className="p-3 text-start font-medium">From</th>
                      <th className="p-3 text-start font-medium">Against budget</th>
                      <th className="p-3 text-end font-medium">Attended</th>
                      <th className="p-3 text-start font-medium">Certificate</th>
                      <th className="p-3 text-start font-medium">Status</th>
                      <th className="p-3 text-end font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {roster.data.map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 align-top">
                        <td className="p-3">{row.employeeName ?? row.employeeCode}</td>
                        <td className="p-3 text-slate-600">
                          {NOMINATION_SOURCE_LABELS[row.source as NominationSource]
                            ?? row.source}
                        </td>
                        <td className="p-3 max-w-xs text-xs text-slate-600">
                          {row.budgetNote ?? '—'}
                        </td>
                        <td className="p-3 text-end">
                          {row.attendancePercent == null
                            ? '—' : `${row.attendancePercent}%`}
                        </td>
                        <td className="p-3 max-w-xs text-xs text-slate-600">
                          {row.certificateNote ?? '—'}
                        </td>
                        <td className="p-3">
                          <StatusBadge
                            status={row.status}
                            tone={NOMINATION_TONE[row.status as NominationStatus] ?? 'neutral'}
                            label={NOMINATION_STATUS_LABELS[row.status as NominationStatus]
                              ?? row.status}
                          />
                        </td>
                        <td className="p-3 text-end">
                          <div className="flex justify-end gap-2">
                            {row.status === 'DRAFT' && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => act(
                                  () => submitNomination.mutateAsync(row.id),
                                  'That nomination could not be submitted')}
                              >
                                Submit
                              </Button>
                            )}
                            {row.status === 'SUBMITTED' && (
                              <Button
                                type="button"
                                onClick={() => act(
                                  () => decideNomination.mutateAsync({
                                    id: row.id, approved: true }),
                                  'That nomination could not be approved')}
                              >
                                Approve
                              </Button>
                            )}
                            {row.status === 'APPROVED' && (
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => act(
                                  () => recordAttendance.mutateAsync({
                                    id: row.id,
                                    body: { attendedOn: chosen.startsOn, present: true },
                                  }),
                                  'That attendance could not be recorded')}
                              >
                                Mark day 1
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <p className="flex items-start gap-2 text-sm text-slate-600">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        The training budget warns; it never blocks a nomination. Where no budget exists for the
        entity and year, the nomination says nothing was checked — which is not the same as
        being within budget.
      </p>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        size="lg"
        title="New session"
        description="Cost is recorded against the training budget, never paid through payroll."
        submitLabel="Save session"
        loading={saveSession.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          act(async () => {
            await saveSession.mutateAsync({
              legalEntityId: entities.data?.[0]?.id ?? null,
              courseId: draft.courseId,
              startsOn: draft.startsOn,
              endsOn: draft.endsOn,
              location: draft.location || null,
              trainer: draft.trainer || null,
              capacity: draft.capacity ? Number(draft.capacity) : null,
              costPerHead: draft.costPerHead ? Number(draft.costPerHead) : null,
              currency: draft.currency || null,
            })
            setDraft(null)
          }, 'That session could not be saved', true)
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="session-course">Course</Label>
              <Select
                id="session-course"
                className="w-full"
                value={draft.courseId}
                onChange={(event) => setDraft({ ...draft, courseId: event.target.value })}
              >
                <option value="">Choose a course…</option>
                {courses.data?.map((course) => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="session-from">From</Label>
              <DatePicker
                id="session-from" value={draft.startsOn}
                onChange={(event) => setDraft({ ...draft, startsOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="session-to">To</Label>
              <DatePicker
                id="session-to" value={draft.endsOn}
                onChange={(event) => setDraft({ ...draft, endsOn: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="session-location">Location</Label>
              <Input
                id="session-location" value={draft.location}
                onChange={(event) => setDraft({ ...draft, location: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="session-trainer">Trainer</Label>
              <Input
                id="session-trainer" value={draft.trainer}
                onChange={(event) => setDraft({ ...draft, trainer: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="session-capacity">Capacity</Label>
              <Input
                id="session-capacity" type="number" value={draft.capacity}
                onChange={(event) => setDraft({ ...draft, capacity: event.target.value })}
              />
              <p className="mt-1 text-xs text-slate-600">Blank means uncapped.</p>
            </div>
            <div>
              <Label htmlFor="session-cost">Cost a head</Label>
              <Input
                id="session-cost" type="number" value={draft.costPerHead}
                onChange={(event) => setDraft({ ...draft, costPerHead: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={nominateDraft != null}
        onOpenChange={(open) => { if (!open) { setNominateDraft(null); setDialogError(null) } }}
        title="Nominate somebody"
        description="Nominating against a training need lets attending close the need."
        submitLabel="Nominate"
        loading={nominate.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!nominateDraft || !selected) return
          act(async () => {
            await nominate.mutateAsync({
              id: selected,
              body: {
                employeeId: nominateDraft.employeeId,
                trainingNeedId: nominateDraft.trainingNeedId || null,
                source: nominateDraft.trainingNeedId ? 'TRAINING_NEED' : 'MANUAL',
              },
            })
            setNominateDraft(null)
          }, 'That nomination could not be made', true)
        }}
      >
        {nominateDraft && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="nominate-employee">Person</Label>
              <EmployeePicker
                  id="nominate-employee"
                  value={nominateDraft.employeeId}
                  onChange={(employeeId) => setNominateDraft({ ...nominateDraft, employeeId: employeeId ?? '' })}
                />
            </div>
            <div>
              <Label htmlFor="nominate-need">Against a training need</Label>
              <Select
                id="nominate-need"
                className="w-full"
                value={nominateDraft.trainingNeedId}
                onChange={(event) =>
                  setNominateDraft({ ...nominateDraft, trainingNeedId: event.target.value })}
              >
                <option value="">Not from a need</option>
                {needs.data?.filter((need) => need.status === 'IDENTIFIED'
                  || need.status === 'NOMINATED').map((need) => (
                  <option key={need.id} value={need.id}>
                    {need.employeeName} — {need.competencyLabel}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        )}
      </FormDialog>

      <FormDialog
        open={cancelDraft != null}
        onOpenChange={(open) => { if (!open) { setCancelDraft(null); setDialogError(null) } }}
        title="Cancel this session"
        description="Say why. People rearranged their week for it."
        submitLabel="Cancel session"
        loading={cancelSession.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!cancelDraft) return
          act(async () => {
            await cancelSession.mutateAsync({
              id: cancelDraft.id, reason: cancelDraft.reason,
            })
            setCancelDraft(null)
          }, 'That session could not be cancelled', true)
        }}
      >
        {cancelDraft && (
          <div>
            <Label htmlFor="cancel-reason">Why</Label>
            <Input
              id="cancel-reason" value={cancelDraft.reason}
              onChange={(event) => setCancelDraft({ ...cancelDraft, reason: event.target.value })}
            />
          </div>
        )}
      </FormDialog>
    </div>
  )
}
